import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';
import { OrganizationPermissions } from '@core/models/organization.models';
import { ToastService } from '@core/services/toast.service';
import { Store } from '@ngxs/store';
import { AuthSelectors } from '@states/auth/auth.selectors';
import { OrganizationState } from '@states/organization/organization.state';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, take, timeout } from 'rxjs/operators';

/**
 * Organization Permission Guard
 *
 * Checks whether the current user has a specific permission
 * within the active organization. Reads the required permission
 * from route data: `{ data: { requiredPermission: 'manageTeam' } }`.
 *
 * - Platform admins always pass.
 * - Organization owners (primaryAdminId) always pass.
 * - Members are checked against their permissions flags.
 * - Denied users are redirected to /host with a toast.
 *
 * The guard is async: it waits for Firebase auth to initialize and then
 * for the active organization to be loaded into NGXS state before
 * evaluating permissions. This prevents a race condition where navigating
 * immediately after sign-in would redirect to /host because the org data
 * hadn't arrived from Firestore yet.
 */
export const orgPermissionGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const store = inject(Store);
  const router = inject(Router);
  const toast = inject(ToastService);

  const requiredPermission = route.data['requiredPermission'] as
    | keyof OrganizationPermissions
    | undefined;

  if (!requiredPermission) {
    // No permission requirement specified — allow access
    return true;
  }

  // Step 1: wait for auth initialization (mirrors hasOrganizationGuard).
  return store.select(AuthSelectors.isInitialized).pipe(
    filter((initialized) => initialized),
    take(1),
    switchMap(() => {
      const user = store.selectSnapshot(AuthSelectors.getUser);

      if (!user) {
        return of(router.createUrlTree(['/sign-in']));
      }

      // Platform admins (ID-token claim, HMM-283) bypass all permission checks
      if (store.selectSnapshot(AuthSelectors.isAdmin) === true) {
        return of(true as const);
      }

      // Step 2: wait for the active organization to be loaded into state.
      // The auth initializer dispatches LoadOrganization fire-and-forget, so
      // org data may arrive after the first navigation attempt.
      return store.select(OrganizationState.activeOrganization).pipe(
        filter((org) => org !== null),
        take(1),
        timeout(8_000),
        catchError(() => of(null)),
        map((org) => {
          if (!org) {
            return router.createUrlTree(['/host']);
          }

          // Organization owner always has full access
          if (org.primaryAdminId === user.id) {
            return true as const;
          }

          // Check the member's permissions in the active organization
          const orgId = org.id;
          const members = store.selectSnapshot(OrganizationState.organizationMembers(orgId));
          const member = members.find((m) => m.userId === user.id && m.isActive);

          if (member && member.permissions[requiredPermission] === true) {
            return true as const;
          }

          // Permission denied
          toast.warning("You don't have permission to access that page.");
          return router.createUrlTree(['/host']);
        }),
      );
    }),
  );
};
